"use client";

import React from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";
import { ChevronRight } from "lucide-react";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,165,0,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const projects = [
  {
    id: 1,
    title: "Cleaning Service Website",
    description: "Professional cleaning service website with service listings, booking flow, and trust-building testimonials.",
    techStack: "React.js, Tailwind CSS",
    image: "/Images/cleaning.png",
    link: "https://cleaning-website-khaki.vercel.app/",
    caseStudySlug: "cleaning-service-website",
  },
  {
    id: 2,
    title: "Security Service Website",
    description: "A robust security company website with service breakdowns, emergency contact integration, and client trust signals.",
    techStack: "React.js, Tailwind CSS, Node.js",
    image: "/Images/securitywebsite.png",
    link: "https://security-website-hazel.vercel.app/",
    caseStudySlug: "security-service-website",
  },
  {
    id: 3,
    title: "Ejazify – Tutoring Platform",
    description: "An online knowledge-sharing platform connecting tutors with students through interactive courses.",
    techStack: "React.js, Tailwind CSS, Node.js",
    image: "/Images/shareyourknowledge.png",
    link: "https://ejazify.org/",
    caseStudySlug: "ejazify-tutoring-platform",
  },
];

// Individual flip card — inline styles used for 3D transforms
// (Tailwind doesn't include perspective/backface-visibility by default)
const FlipCard = ({ project, accentText }) => {
  return (
    <div
      className="group"
      style={{ perspective: "1000px", height: "360px" }}
    >
      {/* Inner wrapper — this is what flips */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        className="group-hover:[transform:rotateY(180deg)]"
      >

        {/* ── FRONT: image only ── */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          className="rounded-xl overflow-hidden border border-green-400/30"
        >
          <img
            src={project.image}
            alt={`${project.title} — Fiieve web development project`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {/* subtle gradient at bottom so it doesn't look plain */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>

        {/* ── BACK: matches OurWorkSection FlipCard ── */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
          className="rounded-xl border border-green-400/40 bg-[#0a1a0a] flex flex-col items-center justify-center gap-4 px-5 text-center"
        >
          {/* Name */}
          <h3 className="text-lg md:text-xl font-semibold text-green-300">
            {project.title}
          </h3>

          {/* Description */}
          <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">
            {project.description}
          </p>

          {/* Tech Stack */}
          <div className="w-full text-center">
            <p className="text-gray-300 text-xs font-semibold uppercase tracking-widest mb-2">
              Tech Stack
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-2">
              {project.techStack.split(",").map((tech) => (
                <li
                  key={tech.trim()}
                  className="flex items-center gap-1.5 text-gray-200 text-sm"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" aria-hidden="true" />
                  {tech.trim()}
                </li>
              ))}
            </ul>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 mt-1">
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View live ${project.title} project`}
              className="inline-flex items-center gap-1.5 bg-green-400 text-black font-semibold text-sm px-5 py-2 rounded-full hover:bg-green-300 transition"
            >
              Live Demo
              <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
            <a
              href={`/portfolio/${project.caseStudySlug}`}
              aria-label={`Read ${project.title} case study`}
              className="inline-flex items-center gap-1.5 border border-green-400/60 text-green-400 font-semibold text-sm px-5 py-2 rounded-full hover:bg-green-400/10 transition"
            >
              Case Study
              <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

const FeaturedSection = () => {
  const { isDark } = useTheme();

  const sectionBg = isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900";
  const accentText = isDark ? "text-green-400" : "text-green-700";
  const subText = isDark ? "text-gray-400" : "text-gray-700";
  const sectionGrid = isDark ? darkGridStyle : lightGridStyle;

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <motion.section
      className={`relative w-full overflow-hidden pt-0 pb-12 md:pb-16 px-6 md:px-12 lg:px-20 transition-colors duration-300 ${sectionBg}`}
      style={sectionGrid}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
      aria-labelledby="featured-projects-heading"
    >
      {/* Section Title */}
      <motion.div className="text-center mb-12" variants={fadeUp}>
        <h2
          id="featured-projects-heading"
          className={`text-3xl md:text-4xl font-bold ${accentText}`}
        >
          Featured Projects
        </h2>
        <p className={`mt-2 max-w-2xl mx-auto ${subText}`}>
          Explore our latest projects showcasing modern designs, tech stacks, and live demos.
        </p>
      </motion.div>

      {/* Projects Grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto"
        variants={containerVariants}
      >
        {projects.map((project) => (
          <motion.div key={project.id} variants={fadeUp}>
            <FlipCard project={project} accentText={accentText} />
          </motion.div>
        ))}
      </motion.div>

      {/* See All Projects CTA */}
      <motion.div className="text-center mt-10" variants={fadeUp}>
        <a
          href="/portfolio"
          aria-label="View all Fiieve projects in our portfolio"
          className="inline-flex items-center gap-2 bg-green-400 text-black font-bold px-8 py-3 rounded-full hover:bg-green-300 transition-all duration-200 hover:scale-105"
        >
          View Our Projects
          <ChevronRight className="w-4 h-4" />
        </a>
      </motion.div>
    </motion.section>
  );
};

export default FeaturedSection;